import { GuestRestaurant, TableFinderParams, TableFinderResult } from '../types'
import { FinderTimeChipCount } from '../constants'

import zonedDateTimeToUtc from './zonedDateTimeToUtc'

function getTime(startsAt: string) {
  return new Date(startsAt).getTime()
}

/**
 * Builds a finder card from the finder response alone. The API already returns
 * each booking type's free arrivals nearest the requested time, chronological
 * and without inquiry times, so the card shows the type whose arrival is
 * nearest the requested time
 */
export default function getTableFinderResult(
  restaurant: GuestRestaurant,
  params: TableFinderParams,
): TableFinderResult {
  const bookableTypes = restaurant.bookingTypes.filter(
    type => type.availableTimes.length > 0,
  )

  if (bookableTypes.length === 0) {
    const waitlistType =
      restaurant.bookingTypes.find(type => type.waitlistEligible) ??
      restaurant.bookingTypes[0]

    return {
      restaurant,
      bookingTypeId: null,
      times: [],
      nextAvailableAt: null,
      waitlistBookingTypeId: waitlistType?.bookingTypeId ?? null,
    }
  }

  const requested = getTime(
    zonedDateTimeToUtc(params.date, params.time, restaurant.timeZoneId),
  )

  function getDistance(availableTimes: string[]) {
    return Math.min(
      ...availableTimes.map(startsAt =>
        Math.abs(getTime(startsAt) - requested),
      ),
    )
  }

  // Stable sort, so a tie keeps the API's booking type order
  const [bookingType] = [...bookableTypes].sort(
    (a, b) => getDistance(a.availableTimes) - getDistance(b.availableTimes),
  )

  const times = [...new Set(bookingType.availableTimes)]
    .sort((a, b) => getTime(a) - getTime(b))
    .slice(0, FinderTimeChipCount)

  return {
    restaurant,
    bookingTypeId: bookingType.bookingTypeId,
    times,
    // The earliest of the times around the requested one
    nextAvailableAt: times[0],
    waitlistBookingTypeId: null,
  }
}
